// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/createAskV2Transport.test.ts
import {
  isStreamSnapshotMessageId,
  runningTurnUserMessageIdFromThread,
} from "./runningTurn";
import {
  type AskV2Attachment,
  type AskV2ChatAck,
  type AskV2ChatApi,
  ASK_V2_QUOTED_CONTEXT_MAX_CHARS,
  ASK_V2_QUOTED_CONTEXT_MAX_COUNT,
  type AskV2CommentContext,
  type AskV2QuotedContext,
  type AskV2SendOpts,
  type AskV2ThreadMessage,
  type AskV2TransportSnapshot,
} from "./types";

export type AskV2Transport = {
  getSnapshot: () => AskV2TransportSnapshot;
  subscribe: (
    listener: (snapshot: AskV2TransportSnapshot) => void,
  ) => () => void;
  reset: () => void;
  hydrateFromServer: (input: {
    chatId: string;
    messages: AskV2ThreadMessage[];
  }) => void;
  syncFromServer: (messages: AskV2ThreadMessage[]) => void;
  setComposer: (text: string) => void;
  setWebSearch: (enabled: boolean) => void;
  addQuotedContext: (input: { text: string; sourceMessageId?: string }) => void;
  removeQuotedContext: (id: string) => void;
  clearQuotedContexts: () => void;
  setCommentContext: (context: AskV2CommentContext | null) => void;
  removeCommentContext: (id: string) => void;
  clearCommentContexts: () => void;
  send: (opts?: AskV2SendOpts) => Promise<void>;
  abort: () => Promise<void>;
  retry: (clientId: string) => Promise<void>;
  beginEdit: (clientId: string) => void;
  setEditDraft: (text: string) => void;
  cancelEdit: () => void;
  submitEdit: () => Promise<void>;
};

const emptySnapshot = (): AskV2TransportSnapshot => ({
  chatId: null,
  composer: "",
  webSearch: false,
  quotedContexts: [],
  commentContexts: [],
  messages: [],
  inFlight: false,
  sending: false,
  aborting: false,
  editingClientId: null,
  editDraft: "",
});

function normalizeQuotedContextText(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= ASK_V2_QUOTED_CONTEXT_MAX_CHARS) return trimmed;
  return `${trimmed.slice(0, ASK_V2_QUOTED_CONTEXT_MAX_CHARS - 3)}...`;
}

function lastStreamingAssistant(
  messages: AskV2ThreadMessage[],
): AskV2ThreadMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role === "assistant" && message.status === "streaming") {
      return message;
    }
  }
  return undefined;
}

function deriveInFlight(
  messages: AskV2ThreadMessage[],
  sending: boolean,
): boolean {
  if (sending) return true;
  if (messages.some((message) => message.status === "streaming")) return true;
  const last = messages[messages.length - 1];
  // why: a locally failed or aborted turn is terminal, not outstanding. Without
  // this, a failed send kept by mergeLocalTail (and a server row aborted
  // locally) reads as live, so the composer shows Stop with no way to retry.
  if (last?.status === "failed" || last?.status === "aborted") return false;
  // Warhol has no pending assistant row; the outstanding turn is "last is user".
  return last?.role === "user";
}

function overlayLocalComposerContext(
  server: AskV2ThreadMessage,
  localMessages: AskV2ThreadMessage[],
): AskV2ThreadMessage {
  if (server.role !== "user") return server;
  const hasComment = (server.commentContexts?.length ?? 0) > 0;
  const hasQuoted = (server.quotedContexts?.length ?? 0) > 0;
  if (hasComment && hasQuoted) return server;
  const match = localMessages.find((message) => {
    if (message.role !== "user") return false;
    if (
      message.serverId &&
      server.serverId &&
      message.serverId === server.serverId
    ) {
      return true;
    }
    if (message.clientId === server.clientId) return true;
    return (
      !message.serverId &&
      message.content === server.content &&
      ((message.commentContexts?.length ?? 0) > 0 ||
        (message.quotedContexts?.length ?? 0) > 0)
    );
  });
  if (!match) return server;
  return {
    ...server,
    commentContexts: hasComment
      ? server.commentContexts
      : match.commentContexts,
    quotedContexts: hasQuoted ? server.quotedContexts : match.quotedContexts,
  };
}

function mergeLocalTail(
  serverMessages: AskV2ThreadMessage[],
  currentMessages: AskV2ThreadMessage[],
): AskV2ThreadMessage[] {
  const serverIds = new Set(
    serverMessages.map((message) => message.serverId ?? message.clientId),
  );
  const localTail = currentMessages.filter((message) => {
    if (message.status === "failed") return true;
    if (message.status !== "sending") return false;
    if (message.serverId && serverIds.has(message.serverId)) return false;
    return true;
  });
  // why: display prefers history; if GET omits comment/quoted context the
  // ACK'd local row is the last copy. Copy it onto the server row before the
  // local sent turn is dropped from the tail.
  const withContext = serverMessages.map((message) =>
    overlayLocalComposerContext(message, currentMessages),
  );
  return [...withContext, ...localTail];
}

function assistantFromAck(
  clientId: string,
  assistant: AskV2ChatAck["assistantMessage"],
): AskV2ThreadMessage {
  // why: live create/send ACKs omit assistantMessage; greeting/demo include
  // it. Empty content (or a missing assistant) is a streaming turn. Content
  // means the assistant already finished (greeting).
  const streaming = assistant == null || assistant.content === "";
  return {
    clientId,
    serverId: assistant?.id,
    role: "assistant",
    content: assistant?.content ?? "",
    status: streaming ? "streaming" : "sent",
  };
}

export function createAskV2Transport(deps: {
  api: AskV2ChatApi;
  id: () => string;
  initialChatId?: string | null;
}): AskV2Transport {
  let snapshot = emptySnapshot();
  // why: chat routes remount with a known id; without seeding, send() would
  // createChat and orphan the URL chat.
  if (deps.initialChatId) {
    snapshot = { ...snapshot, chatId: deps.initialChatId };
  }
  let turnGeneration = 0;
  // why: a stale ACK after Stop must not clear `sending` that a newer send() claimed.
  let sendingOwnerGeneration: number | null = null;
  const listeners = new Set<(next: AskV2TransportSnapshot) => void>();

  function claimSending(generation: number): void {
    sendingOwnerGeneration = generation;
  }

  function dropIfStale(generation: number): boolean {
    if (generation === turnGeneration) return false;
    mutate((current) => {
      if (sendingOwnerGeneration !== generation) return current;
      sendingOwnerGeneration = null;
      return { ...current, sending: false };
    });
    return true;
  }

  function emit(next: AskV2TransportSnapshot): void {
    snapshot = next;
    listeners.forEach((listener) => listener(snapshot));
  }

  function mutate(
    updater: (current: AskV2TransportSnapshot) => AskV2TransportSnapshot,
  ): AskV2TransportSnapshot {
    const next = updater(snapshot);
    // why: returning current is a no-op; emit would still re-render (same finding).
    if (next === snapshot) return snapshot;
    emit(next);
    return snapshot;
  }

  function applyAck(args: {
    userClientId: string;
    chatId: string;
    userServerId: string;
    userContent: string;
    assistant?: { id: string; content: string };
    clearComposer: boolean;
    webSearchToClear?: boolean;
    clearEdit: boolean;
  }): void {
    const assistant = assistantFromAck(deps.id(), args.assistant);
    sendingOwnerGeneration = null;
    mutate((current) => ({
      ...current,
      chatId: args.chatId,
      composer: args.clearComposer ? "" : current.composer,
      // why: the ACK belongs to the selection captured for this turn. If the
      // person changed the toggle while the request was pending, that newer
      // choice belongs to the next turn and must survive this ACK.
      webSearch:
        args.webSearchToClear === current.webSearch ? false : current.webSearch,
      quotedContexts: args.clearComposer ? [] : current.quotedContexts,
      commentContexts: args.clearComposer ? [] : current.commentContexts,
      sending: false,
      inFlight: assistant.status === "streaming",
      editingClientId: args.clearEdit ? null : current.editingClientId,
      editDraft: args.clearEdit ? "" : current.editDraft,
      messages: [
        ...current.messages.map((message) =>
          message.clientId === args.userClientId
            ? {
                ...message,
                serverId: args.userServerId,
                content: args.userContent,
                status: "sent" as const,
              }
            : message,
        ),
        assistant,
      ],
    }));
  }

  async function abortInFlight(opts?: {
    includeSendingUser?: boolean;
    bumpGeneration?: boolean;
  }): Promise<void> {
    // why: submitEdit aborts the prior turn without consuming Stop; only user abort() bumps.
    if (opts?.bumpGeneration !== false) {
      turnGeneration += 1;
    }
    const generationAtEntry = turnGeneration;
    const includeSendingUser = opts?.includeSendingUser ?? true;
    const assistant = lastStreamingAssistant(snapshot.messages);
    const chatId = snapshot.chatId;
    // why: warhol writes christo's assistant row at the turn's FIRST TOOL CALL,
    // many seconds after the first words are on screen, and the live send ACK
    // omits `assistantMessage` (`assistantFromAck`). Gating on
    // `assistant?.serverId` alone means no abort is POSTed for that whole
    // window, so Stop does nothing and the turn runs to completion. Two steps
    // fix it, and both are needed:
    //   - the list read here is the OVERLAID one, so in that window the only
    //     `streaming` assistant row is `applyStreamSnapshot`'s own, keyed by
    //     the stream address `w<userMessageId>`. That is truthy but is not an
    //     ObjectId, so christo's `commitPartialMessage` bails on
    //     `!Types.ObjectId.isValid(messageId)` and the partial words are lost —
    //     reject the address rather than send it;
    //   - fall back to the turn's own message. The cancel is chat-scoped
    //     (`abortWarholTurn(enterpriseId, chatId)`), so any real message of the
    //     running turn reaches it — the same fallback V1 Stop makes with
    //     `findStopTargetMessageId`.
    const assistantServerId = isStreamSnapshotMessageId(assistant?.serverId)
      ? undefined
      : assistant?.serverId;
    const messageId =
      assistantServerId ??
      runningTurnUserMessageIdFromThread(snapshot.messages) ??
      undefined;

    mutate((current) => ({ ...current, aborting: true }));
    try {
      if (chatId && messageId) {
        await deps.api.abortMessage({
          chatId,
          messageId,
        });
      }
    } catch {
      // why: Stop / edit-last must still unstick the composer if abort fails.
    } finally {
      // why: Stop during this await bumps turnGeneration. Keep sending locked
      // only when this abort still owns the generation (submitEdit's prior-turn abort).
      const superseded = turnGeneration !== generationAtEntry;
      const clearSending = includeSendingUser || superseded;
      if (clearSending) sendingOwnerGeneration = null;
      mutate((current) => ({
        ...current,
        aborting: false,
        inFlight: false,
        sending: clearSending ? false : current.sending,
        messages: current.messages.map((message) => {
          if (
            assistant &&
            message.clientId === assistant.clientId &&
            message.status === "streaming"
          ) {
            return { ...message, status: "aborted" as const };
          }
          if (
            includeSendingUser &&
            message.role === "user" &&
            message.status === "sending"
          ) {
            return { ...message, status: "aborted" as const };
          }
          return message;
        }),
      }));
    }
  }

  async function dispatchUserTurn(args: {
    clientId: string;
    text: string;
    attachments?: AskV2Attachment[];
    quotedContexts?: AskV2QuotedContext[];
    webSearch?: boolean;
    commentContexts?: AskV2CommentContext[];
    mentions?: string[];
    mentionEmails?: string[];
    clearComposerOnAck: boolean;
    webSearchToClearOnAck?: boolean;
    generation: number;
  }): Promise<void> {
    const chatId = snapshot.chatId;
    const attachments =
      args.attachments && args.attachments.length > 0
        ? args.attachments
        : undefined;
    const quotedContexts =
      args.quotedContexts && args.quotedContexts.length > 0
        ? args.quotedContexts
        : undefined;
    const commentContexts =
      args.commentContexts && args.commentContexts.length > 0
        ? args.commentContexts
        : undefined;
    const mentions =
      args.mentions && args.mentions.length > 0 ? args.mentions : undefined;
    const mentionEmails =
      args.mentionEmails && args.mentionEmails.length > 0
        ? args.mentionEmails
        : undefined;
    try {
      const result = chatId
        ? await deps.api.sendMessage({
            chatId,
            message: args.text,
            ...(attachments ? { attachments } : {}),
            ...(quotedContexts ? { quotedContexts } : {}),
            ...(args.webSearch ? { webSearch: true } : {}),
            ...(commentContexts ? { commentContexts } : {}),
            ...(mentions ? { mentions } : {}),
            ...(mentionEmails ? { mentionEmails } : {}),
          })
        : await deps.api.createChat({
            message: args.text,
            ...(attachments ? { attachments } : {}),
            ...(quotedContexts ? { quotedContexts } : {}),
            ...(args.webSearch ? { webSearch: true } : {}),
            ...(commentContexts ? { commentContexts } : {}),
            ...(mentions ? { mentions } : {}),
            ...(mentionEmails ? { mentionEmails } : {}),
          });
      if (dropIfStale(args.generation)) return;
      const nextChatId = result.chatId ?? chatId;
      if (!nextChatId) {
        throw new Error("Ask V2 send ACK missing chatId");
      }
      applyAck({
        userClientId: args.clientId,
        chatId: nextChatId,
        userServerId: result.userMessage.id,
        userContent: result.userMessage.content,
        assistant: result.assistantMessage,
        clearComposer: args.clearComposerOnAck,
        webSearchToClear: args.webSearchToClearOnAck,
        clearEdit: false,
      });
    } catch {
      if (dropIfStale(args.generation)) return;
      sendingOwnerGeneration = null;
      mutate((current) => ({
        ...current,
        sending: false,
        messages: current.messages.map((message) =>
          message.clientId === args.clientId
            ? { ...message, status: "failed" as const }
            : message,
        ),
      }));
    }
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    reset() {
      mutate(() => emptySnapshot());
    },
    hydrateFromServer({ chatId, messages }) {
      mutate((current) => ({
        ...current,
        chatId,
        messages,
        inFlight: deriveInFlight(messages, current.sending),
      }));
    },
    syncFromServer(messages) {
      mutate((current) => {
        const merged = mergeLocalTail(messages, current.messages);
        return {
          ...current,
          messages: merged,
          inFlight: deriveInFlight(merged, current.sending),
        };
      });
    },
    setComposer(text) {
      mutate((current) => ({ ...current, composer: text }));
    },
    setWebSearch(enabled) {
      mutate((current) => ({ ...current, webSearch: enabled }));
    },
    addQuotedContext(input) {
      const text = normalizeQuotedContextText(input.text);
      if (!text) return;
      // why: the server schema caps the array at ASK_PUNTT_QUOTED_CONTEXT_MAX_COUNT
      // (`askPunttQuotedContextsSchema.max(...)`). Appending past it would build a
      // request the server rejects with a 400, failing the whole turn rather than
      // the extra quote — so refuse the append once we are at the cap.
      if (snapshot.quotedContexts.length >= ASK_V2_QUOTED_CONTEXT_MAX_COUNT) {
        return;
      }
      const next: AskV2QuotedContext = {
        id: deps.id(),
        text,
        sourceMessageId: input.sourceMessageId,
      };
      mutate((current) => ({
        ...current,
        quotedContexts: [...current.quotedContexts, next],
      }));
    },
    removeQuotedContext(id) {
      mutate((current) => ({
        ...current,
        quotedContexts: current.quotedContexts.filter((ctx) => ctx.id !== id),
      }));
    },
    clearQuotedContexts() {
      mutate((current) =>
        current.quotedContexts.length === 0
          ? current
          : { ...current, quotedContexts: [] },
      );
    },
    setCommentContext(context) {
      mutate((current) => {
        if (!context) {
          return current.commentContexts.length === 0
            ? current
            : { ...current, commentContexts: [] };
        }
        const existing = current.commentContexts[0];
        if (
          existing?.id === context.id &&
          existing.commentId === context.commentId &&
          existing.reviewId === context.reviewId &&
          existing.number === context.number &&
          existing.title === context.title &&
          existing.tag === context.tag
        ) {
          return current;
        }
        return { ...current, commentContexts: [context] };
      });
    },
    removeCommentContext(id) {
      mutate((current) => ({
        ...current,
        commentContexts: current.commentContexts.filter((ctx) => ctx.id !== id),
      }));
    },
    clearCommentContexts() {
      mutate((current) =>
        current.commentContexts.length === 0
          ? current
          : { ...current, commentContexts: [] },
      );
    },
    async send(opts) {
      const usingMessageOverride = opts?.message != null;
      const text = (opts?.message ?? snapshot.composer).trim();
      const attachments = opts?.attachments ?? [];
      const mentions =
        opts?.mentions && opts.mentions.length > 0
          ? [...opts.mentions]
          : undefined;
      const mentionEmails =
        opts?.mentionEmails && opts.mentionEmails.length > 0
          ? [...opts.mentionEmails]
          : undefined;
      const quotedContexts =
        snapshot.quotedContexts.length > 0
          ? [...snapshot.quotedContexts]
          : undefined;
      const webSearch = snapshot.webSearch;
      const commentContexts =
        snapshot.commentContexts.length > 0
          ? [...snapshot.commentContexts]
          : undefined;
      // why: V1 lets a warhol review start with files and no typed prompt
      if (
        !text &&
        attachments.length === 0 &&
        !quotedContexts?.length &&
        !commentContexts?.length
      ) {
        return;
      }

      // Split out of the guard above so an in-flight drop is never silent. An
      // answer form the agent drew mid-turn is live (it is the last turn) but
      // cannot send: the turn that drew it has not settled. Neither the person
      // nor the agent is told, so this warning is the only trace. Nothing is
      // cleared here — the composer is emptied later, in dispatchUserTurn, so
      // returning keeps whatever they had typed.
      if (snapshot.sending || snapshot.inFlight) {
        console.warn("[ask-v2] send() dropped: a turn is already in flight");
        return;
      }

      const clientId = deps.id();
      const generation = turnGeneration;
      claimSending(generation);
      mutate((current) => ({
        ...current,
        sending: true,
        commentContexts: [],
        messages: [
          ...current.messages,
          {
            clientId,
            role: "user",
            content: text,
            status: "sending",
            attachments: attachments.length > 0 ? attachments : undefined,
            quotedContexts,
            ...(webSearch ? { webSearch: true } : {}),
            commentContexts,
            // why: retry()/submitEdit() rebuild the turn from the stored
            // message only. Without these, a retried @mention never reaches
            // christo's access-grant path and the invitee silently loses access.
            mentions,
            mentionEmails,
          },
        ],
      }));

      await dispatchUserTurn({
        clientId,
        text,
        attachments,
        quotedContexts,
        webSearch,
        commentContexts,
        mentions,
        mentionEmails,
        // why: form answers bypass the composer; clearing it would wipe an
        // in-progress draft the person was still typing.
        clearComposerOnAck: !usingMessageOverride,
        webSearchToClearOnAck: webSearch,
        generation,
      });
    },
    abort: () => abortInFlight({ includeSendingUser: true }),
    async retry(clientId) {
      const failed = snapshot.messages.find(
        (message) =>
          message.clientId === clientId && message.status === "failed",
      );
      if (!failed || snapshot.sending || snapshot.inFlight) return;

      const generation = turnGeneration;
      const clearDraft = snapshot.composer.trim() === failed.content;
      claimSending(generation);
      mutate((current) => ({
        ...current,
        sending: true,
        messages: current.messages.map((message) =>
          message.clientId === clientId
            ? { ...message, status: "sending" as const }
            : message,
        ),
      }));

      await dispatchUserTurn({
        clientId,
        text: failed.content,
        attachments: failed.attachments,
        quotedContexts: failed.quotedContexts,
        webSearch: failed.webSearch,
        commentContexts: failed.commentContexts,
        clearComposerOnAck: clearDraft,
        webSearchToClearOnAck: Boolean(failed.webSearch),
        mentions: failed.mentions,
        mentionEmails: failed.mentionEmails,
        generation,
      });
    },
    beginEdit(clientId) {
      const message = snapshot.messages.find(
        (item) => item.clientId === clientId,
      );
      if (!message || message.role !== "user") return;
      mutate((current) => ({
        ...current,
        editingClientId: clientId,
        editDraft: message.content,
      }));
    },
    setEditDraft(text) {
      mutate((current) => ({ ...current, editDraft: text }));
    },
    cancelEdit() {
      mutate((current) => ({
        ...current,
        editingClientId: null,
        editDraft: "",
      }));
    },
    async submitEdit() {
      const editingClientId = snapshot.editingClientId;
      const content = snapshot.editDraft.trim();
      if (!editingClientId || !content || snapshot.sending) return;

      const target = snapshot.messages.find(
        (message) => message.clientId === editingClientId,
      );
      if (!target || target.role !== "user") return;

      // why: capture before aborting the prior turn so a Stop during that abort still drops the regenerate ACK.
      const generation = turnGeneration;
      claimSending(generation);
      mutate((current) => ({
        ...current,
        sending: true,
        messages: current.messages.map((message) =>
          message.clientId === editingClientId
            ? { ...message, content, status: "sending" as const }
            : message,
        ),
      }));
      if (snapshot.inFlight) {
        await abortInFlight({
          includeSendingUser: false,
          bumpGeneration: false,
        });
      }

      if (!snapshot.chatId || !target.serverId) {
        await dispatchUserTurn({
          clientId: editingClientId,
          text: content,
          mentions: target.mentions,
          mentionEmails: target.mentionEmails,
          clearComposerOnAck: false,
          webSearchToClearOnAck: undefined,
          generation,
        });
        if (
          snapshot.messages.find(
            (message) => message.clientId === editingClientId,
          )?.status === "sent"
        ) {
          mutate((current) => ({
            ...current,
            editingClientId: null,
            editDraft: "",
          }));
        }
        return;
      }

      try {
        const result = await deps.api.regenerateMessage({
          chatId: snapshot.chatId,
          messageId: target.serverId,
          content,
        });
        // why: a Stop (or newer turn) between here and the ACK bumps the
        // generation. Dropping the stale ACK keeps the abort honored instead
        // of resurrecting the cancelled streaming turn. dropIfStale also
        // unlocks the composer when this generation still owns `sending`.
        if (dropIfStale(generation)) return;
        applyAck({
          userClientId: editingClientId,
          chatId: snapshot.chatId,
          userServerId: result.userMessage.id,
          userContent: result.userMessage.content,
          assistant: result.assistantMessage,
          clearComposer: false,
          webSearchToClear: undefined,
          clearEdit: true,
        });
      } catch {
        if (dropIfStale(generation)) return;
        sendingOwnerGeneration = null;
        mutate((current) => ({
          ...current,
          sending: false,
          messages: current.messages.map((message) =>
            message.clientId === editingClientId
              ? { ...message, status: "failed" as const }
              : message,
          ),
        }));
      }
    },
  };
}
